import React, { useMemo } from 'react';
import { TrendingUp, Sparkles, Flame, ArrowRight, ChevronRight } from 'lucide-react';
import { useFontSizes } from '../../contexts/FontSizeContext';
import { useI18n } from '../../i18n/I18nContext';
import { FONT_SIZE_CLASS, WIDGET_SIZE_FONTS, WIDGET_SIZE_PADDING } from '../../utils/types';
import type { FontSizeScale, TrendingDisplayVariant } from '../../utils/types';
import type { WidgetProps } from './types';

interface TrendingItem {
  query: string;
  resolvedTo?: string;
}

export const TrendingSearchesWidget: React.FC<WidgetProps> = ({ config, defaults, onSearch }) => {
  const globalFonts = useFontSizes();
  const { t } = useI18n();

  // Resolve effective font sizes: per-widget fontSizes > size preset > global
  const { heading, body, small } = useMemo(() => {
    const presetFonts = config.size ? WIDGET_SIZE_FONTS[config.size] : undefined;
    const resolve = (
      override: FontSizeScale | undefined,
      preset: FontSizeScale | undefined,
      fallback: string,
    ) => {
      const scale = override ?? preset;
      return scale ? FONT_SIZE_CLASS[scale] : fallback;
    };
    return {
      heading: resolve(config.fontSizes?.heading, presetFonts?.heading, globalFonts.heading),
      body: resolve(config.fontSizes?.body, presetFonts?.body, globalFonts.body),
      small: resolve(config.fontSizes?.small, presetFonts?.small, globalFonts.small),
    };
  }, [config.size, config.fontSizes, globalFonts]);

  const isTrending = (defaults?.trendingSearches?.length ?? 0) > 0;

  const items: TrendingItem[] = isTrending
    ? (defaults?.trendingSearches ?? [])
    : (defaults?.popularSearches ?? []).map((s) => ({ query: s }));

  const visible = config.maxItems ? items.slice(0, config.maxItems) : items;
  if (visible.length === 0) return null;

  const variant: TrendingDisplayVariant = config.trendingVariant ?? 'chips';
  const iconSize = config.size === 'compact' ? 'h-3 w-3' : config.size === 'large' ? 'h-5 w-5' : 'h-4 w-4';
  const HeroIcon = isTrending ? Flame : Sparkles;

  const header = (
    <h2 className={`flex items-center gap-2 ${heading} font-semibold uppercase tracking-wider text-[#020202] mb-3 sm:mb-4`}>
      <TrendingUp className={iconSize} />
      {config.title ?? (isTrending ? t('section.trending') : t('section.popularSearches'))}
    </h2>
  );

  /* -------------------- Variant: chips (polished, horizontal scroll) -------------------- */
  if (variant === 'chips') {
    return (
      <section className="mb-4 sm:mb-6">
        {header}
        <div className="flex gap-1.5 overflow-x-auto pb-1.5 -mx-3 px-3 sm:-mx-0 sm:px-0 botilia-scrollbar">
          {visible.map((item, index) => {
            const searchTerm = item.resolvedTo ?? item.query;
            const isHero = index === 0;
            return (
              <button
                key={item.query}
                type="button"
                onClick={() => onSearch(searchTerm)}
                className={`group flex-shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-all animate-fade-in
                  ${isHero
                    ? 'bg-gradient-to-r from-[#f02e69] to-[#ff5e8a] text-white shadow-sm shadow-[#f02e69]/25 hover:shadow-md hover:shadow-[#f02e69]/40'
                    : 'bg-white border border-[#f02e69]/15 text-[#1a1a1a] shadow-sm hover:border-[#f02e69]/40 hover:shadow-md'}
                `}
                style={{ animationDelay: `${index * 30}ms` }}
              >
                <span className={`inline-flex items-center justify-center rounded-full font-bold ${isHero ? 'bg-white/25 text-white' : 'bg-[#f02e69]/10 text-[#f02e69]'} w-4 h-4 text-[9px]`}>
                  {index + 1}
                </span>
                <HeroIcon className={`h-3 w-3 ${isHero ? 'text-white' : 'text-[#f02e69]'}`} />
                {item.resolvedTo ? (
                  <>
                    <span className={`text-[10px] line-through ${isHero ? 'text-white/70' : 'text-gray-400'}`}>{item.query}</span>
                    <ArrowRight className={`h-2.5 w-2.5 ${isHero ? 'text-white/80' : 'text-gray-400'}`} />
                    <span className="whitespace-nowrap">{item.resolvedTo}</span>
                  </>
                ) : (
                  <span className="whitespace-nowrap">{item.query}</span>
                )}
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  /* -------------------- Variant: numbered-list -------------------- */
  if (variant === 'numbered-list') {
    return (
      <section className="mb-4 sm:mb-8">
        {header}
        <ol className="flex flex-col divide-y divide-gray-100 rounded-2xl border border-gray-100 bg-white/60 backdrop-blur-sm shadow-sm overflow-hidden">
          {visible.map((item, index) => {
            const searchTerm = item.resolvedTo ?? item.query;
            const rank = index + 1;
            const isTop3 = index < 3;
            return (
              <li key={item.query}>
                <button
                  type="button"
                  onClick={() => onSearch(searchTerm)}
                  className="w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-3.5 text-left hover:bg-[#f02e69]/[0.04] transition-colors group animate-fade-in"
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <span
                    className={`flex-shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center font-bold text-sm ${
                      isTop3
                        ? 'bg-gradient-to-br from-[#f02e69] to-[#ff5e8a] text-white shadow-md shadow-[#f02e69]/30'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {rank}
                  </span>
                  <div className="flex-1 min-w-0 flex items-center gap-2">
                    {isTop3 && <HeroIcon className={`${iconSize} text-[#f02e69] flex-shrink-0`} />}
                    {item.resolvedTo ? (
                      <span className={`truncate ${body}`}>
                        <span className={`${small} text-gray-400 line-through mr-1.5`}>{item.query}</span>
                        <span className="font-semibold text-[#1a1a1a]">{item.resolvedTo}</span>
                      </span>
                    ) : (
                      <span className={`truncate ${body} font-semibold text-[#1a1a1a]`}>{item.query}</span>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-300 group-hover:text-[#f02e69] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                </button>
              </li>
            );
          })}
        </ol>
      </section>
    );
  }

  /* -------------------- Variant: tile-row (horizontal scroll) -------------------- */
  return (
    <section className="mb-4 sm:mb-8">
      {header}
      <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-3 -mx-3 px-3 sm:-mx-0 sm:px-0 snap-x snap-mandatory botilia-scrollbar">
        {visible.map((item, index) => {
          const searchTerm = item.resolvedTo ?? item.query;
          const rank = index + 1;
          const isHero = index === 0;
          return (
            <button
              key={item.query}
              type="button"
              onClick={() => onSearch(searchTerm)}
              className={`flex-shrink-0 snap-start w-[180px] sm:w-[220px] rounded-2xl p-4 sm:p-5 text-left transition-all animate-fade-in hover:-translate-y-1
                ${isHero
                  ? 'bg-gradient-to-br from-[#f02e69] via-[#ff4578] to-[#ff5e8a] text-white shadow-xl shadow-[#f02e69]/30'
                  : 'bg-gradient-to-br from-white to-[#fef5f8] border border-[#f02e69]/10 text-[#1a1a1a] shadow-md hover:shadow-lg hover:border-[#f02e69]/30'}
              `}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start justify-between mb-3">
                <span
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-xl font-bold text-base ${
                    isHero ? 'bg-white/25 text-white backdrop-blur-sm' : 'bg-[#f02e69]/10 text-[#f02e69]'
                  }`}
                >
                  #{rank}
                </span>
                <HeroIcon className={`h-5 w-5 ${isHero ? 'text-white' : 'text-[#f02e69]'}`} />
              </div>
              {item.resolvedTo ? (
                <div className="space-y-1">
                  <div className={`${small} ${isHero ? 'text-white/70' : 'text-gray-400'} line-through truncate`}>
                    {item.query}
                  </div>
                  <div className={`${body} font-bold leading-tight line-clamp-2`}>{item.resolvedTo}</div>
                </div>
              ) : (
                <div className={`${body} font-bold leading-tight line-clamp-2`}>{item.query}</div>
              )}
              <div className={`mt-3 flex items-center gap-1 text-xs font-medium ${isHero ? 'text-white/90' : 'text-[#f02e69]'}`}>
                <ArrowRight className="h-3.5 w-3.5" />
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};
